import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";

export default function Register() {
  const { t } = useLanguage();
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    iban: "", tax_code: "",
    address_street: "", address_city: "", address_zip: "", phone: "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const canProceed = () =>
    form.name.trim().length >= 1 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) &&
    form.password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    // Validazioni base italiane
    const iban = form.iban.replace(/\s+/g, "").toUpperCase();
    if (iban.length < 15 || !/^[A-Z]{2}/.test(iban)) {
      toast.error(t("IBAN non valido (deve iniziare con 2 lettere di codice paese)"));
      return;
    }
    const cf = form.tax_code.replace(/\s+/g, "").toUpperCase();
    if (cf.length < 11) {
      toast.error(t("Codice fiscale/P.IVA non valido"));
      return;
    }
    if (form.address_zip.length < 4) {
      toast.error(t("CAP non valido"));
      return;
    }
    setBusy(true);
    try {
      const res = await register({ ...form, iban, tax_code: cf });
      if (res?.email_sent) {
        toast.success(t("Account creato. Controlla la tua email per confermare l'indirizzo."));
      } else {
        toast.success(t("Account creato. La verifica email sarà attiva a breve."));
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Errore registrazione"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2">
      <Seo title="Crea il tuo account Rarium" description="Registrati su Rarium — accesso gratuito ai lotti d'asta curati per collezionisti." path="/register" />
      <div className="hidden lg:block bg-stone-900 relative overflow-hidden grain">
        <img src="https://images.unsplash.com/photo-1605100804763-247f67b3557e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxkaWFtb25kJTIwamV3ZWxyeSUyMGVsZWdhbnR8ZW58MHx8fHwxNzg2OTQ4NDU0fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-10 left-10 text-stone-50 max-w-md">
          <div className="overline text-[#B49A5F]">{t("Diventa collezionista Rarium")}</div>
          <div className="font-serif text-4xl mt-2 leading-tight">{t("Registrazione gratuita, autenticazione europea.")}</div>
        </div>
      </div>

      <div className="flex items-start justify-center p-8 lg:p-16 overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="overline text-stone-500">{t("Crea il tuo account · Step ")}{step}{t(" di 2")}</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-2">
            {step === 1 ? t("Unisciti alla lista.") : t("Ultimi dettagli.")}
          </h1>

          <div className="mt-8 h-1 bg-stone-200 relative">
            <div className="h-1 bg-[#8C3A3A] transition-all duration-500" style={{ width: step === 1 ? "50%" : "100%" }} />
          </div>

          {step === 1 ? (
            <div className="mt-8 flex flex-col gap-5">
              <Field label={t("Nome e cognome")} testId="register-name" value={form.name} onChange={upd("name")} required />
              <Field label="Email" type="email" testId="register-email" value={form.email} onChange={upd("email")} required />
              <Field label={t("Password (min. 8 caratteri)")} type="password" testId="register-password" value={form.password} onChange={upd("password")} required minLength={8} />
              <button
                type="button"
                data-testid="register-next"
                disabled={!canProceed()}
                onClick={() => setStep(2)}
                className="mt-4 text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-8 py-4 transition-colors disabled:opacity-50"
              >{t("Continua")}</button>
              <div className="text-xs text-stone-500 leading-relaxed">
                {t("Ti serviranno anche IBAN, codice fiscale e indirizzo di residenza per completare la registrazione. Questi dati sono richiesti per il pagamento dei venditori e la conformità fiscale italiana.")}
              </div>
            </div>
          ) : (
            <div className="mt-8 flex flex-col gap-5">
              <Field label={t("IBAN (per accrediti come venditore)")} testId="register-iban" value={form.iban} onChange={upd("iban")} required placeholder="IT60 X054 2811 1010 0000 0123 456" />
              <Field label={t("Codice Fiscale o Partita IVA")} testId="register-taxcode" value={form.tax_code} onChange={upd("tax_code")} required placeholder="RSSMRA80A01H501U" />
              <Field label={t("Indirizzo (via e numero)")} testId="register-street" value={form.address_street} onChange={upd("address_street")} required placeholder="Via Roma, 42" />
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("Città")} testId="register-city" value={form.address_city} onChange={upd("address_city")} required />
                <Field label={t("CAP")} testId="register-zip" value={form.address_zip} onChange={upd("address_zip")} required />
              </div>
              <Field label={t("Telefono (opzionale)")} type="tel" testId="register-phone" value={form.phone} onChange={upd("phone")} placeholder="+39 331 8675514" />

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs uppercase tracking-widest font-semibold border border-stone-900 hover:bg-stone-100 px-6 py-4 transition-colors"
                >{t("Indietro")}</button>
                <button
                  type="submit"
                  data-testid="register-submit"
                  disabled={busy}
                  className="flex-1 text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-8 py-4 transition-colors disabled:opacity-50"
                >{busy ? "…" : t("Crea account")}</button>
              </div>
              <div className="text-[11px] text-stone-500 leading-relaxed">
                {t("Ti invieremo una email di conferma. Puoi navigare le aste da subito, ma per pubblicare lotti l'email deve essere verificata.")}
              </div>
            </div>
          )}
          <div className="mt-6 text-sm text-stone-600">
            {t("Hai già un account?")} <Link to="/login" className="underline-hover text-[#8C3A3A]">{t("Accedi")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, testId, ...props }) => (
  <label className="flex flex-col">
    <span className="overline text-stone-500 mb-2">{label}</span>
    <input data-testid={testId} {...props} className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none" />
  </label>
);
